import * as React from 'react';
import { useWindowSize } from '@/hooks/useWindowSize';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  FloatingPortal,
  FloatingFocusManager,
  FloatingArrow,
  arrow,
  useHover,
} from '@floating-ui/react';

const ARROW_HEIGHT = 7;
const GAP = 2;

/** @returns {any} */
export function useTooltip({
  initialOpen = false,
  placement = 'bottom',
  modal,
  delay,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  disabled = false,
} = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
  const [labelId, setLabelId] = React.useState();
  const [descriptionId, setDescriptionId] = React.useState();
  const arrowRef = React.useRef(null);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open: disabled ? false : open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      arrow({
        element: arrowRef,
        padding: 0,
      }),
      offset(ARROW_HEIGHT + GAP),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'end',
        padding: 5,
      }),
      shift({ padding: 5 }),
    ],
  });

  const context = data.context;

  const hover = useHover(context, {
    enabled: !disabled && controlledOpen == null,
    delay: { close: delay },
  });
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const interactions = useInteractions([hover, dismiss, role]);

  return React.useMemo(
    () => ({
      open,
      arrowRef,
      setOpen,
      disabled,
      ...interactions,
      ...data,
      modal,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
    }),
    [
      open,
      setOpen,
      interactions,
      data,
      modal,
      labelId,
      descriptionId,
      arrowRef,
    ],
  );
}

const TooltipContext = React.createContext(null);

export const useTooltipContext = () => {
  const context = React.useContext(TooltipContext);

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};

export function Tooltip({ children, modal = false, ...restOptions }) {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const popover = useTooltip({ modal, ...restOptions });
  return (
    <TooltipContext.Provider value={popover}>
      {children}
    </TooltipContext.Provider>
  );
}

export const TooltipTrigger = React.forwardRef(function TooltipTrigger(
  { children, asChild = false, ...props },
  propRef,
) {
  const context = useTooltipContext();
  const childrenRef = children.ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...children.props,
        'data-state': context.open ? 'open' : 'closed',
        'aria-disabled': context.disabled || undefined,
        ...(!context.disabled ? context.getReferenceProps() : {}),
      }),
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      // The user can style the trigger based on the state
      data-state={context.open ? 'open' : 'closed'}
      aria-disabled={context.disabled || undefined}
      {...context.getReferenceProps(props)}
    >
      {children}
    </button>
  );
});

export const TooltipContent = React.forwardRef(function TooltipContent(
  { style, arrowColor, ...props },
  propRef,
) {
  const {
    context: floatingContext,
    arrowRef,
    disabled,
    ...context
  } = useTooltipContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);
  const { isMobile } = useWindowSize();

  if (disabled || !floatingContext.open) return null;

  return (
    <FloatingPortal>
      <FloatingFocusManager context={floatingContext} modal={context.modal}>
        <div
          ref={ref}
          style={{ ...context.floatingStyles, ...style }}
          aria-labelledby={context.labelId}
          aria-describedby={context.descriptionId}
          {...context.getFloatingProps(props)}
        >
          {props.children}
          {!isMobile && (
            <FloatingArrow
              ref={arrowRef}
              context={floatingContext}
              fill={arrowColor}
            />
          )}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
});
